import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { UserManagementDTO } from '../../../../dto/users/user-management.dto';
import { UserEmailDTO } from '../../../../dto/users/user-email.dto';
import {
  AccessGroupDTO,
  UserAccessGroupSummaryDTO,
} from '../../../../dto/access-group/access-group.dto';
import { environment } from '../../../../environment/environment';

@Component({
  selector: 'app-user-email-management-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatListModule,
    MatChipsModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  template: `
    <div class="email-management-dialog">
      <h2 mat-dialog-title>
        <mat-icon>email</mat-icon>
        Email Administration - {{ data.user.first_name }}
        {{ data.user.last_name }}
      </h2>

      <mat-dialog-content>
        <!-- Warning Banner -->
        @if (!data.readOnly) {
        <div class="warning-banner">
          <mat-icon>warning</mat-icon>
          <div>
            <strong>Advarsel:</strong> Vær forsigtig når du tilføjer emails til
            brugere. Sørg for at det er samme person, og at emailen ikke
            tilhører en anden bruger.
          </div>
        </div>
        }

        <!-- Read-only Banner -->
        @if (data.readOnly) {
        <div class="info-banner">
          <mat-icon>info</mat-icon>
          <div>
            <strong>Skrivebeskyttet:</strong> Denne bruger har en beskyttet
            rolle @if (data.user.protected_roles &&
            data.user.protected_roles.length > 0) { ({{
              formatRoleNames(data.user.protected_roles)
            }}) } @if (!data.user.protected_roles ||
            data.user.protected_roles.length === 0) { (orgbruger, orgadmin,
            superadmin) } og kan ikke redigeres.
          </div>
        </div>
        }

        <!-- Primary Email -->
        <mat-card class="email-card">
          <mat-card-header>
            <mat-card-title>Primær Email</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="primary-email">
              <mat-icon>star</mat-icon>
              <span class="email-text">{{
                data.user.user || 'Ingen email'
              }}</span>
              @if (data.user.user) {
              <mat-chip>Primær</mat-chip>
              }
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Secondary Emails -->
        <mat-card class="email-card">
          <mat-card-header>
            <mat-card-title>Sekundære Emails</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            @if (secondaryEmails.length > 0) {
            <div class="secondary-emails-list">
              <mat-list>
                @for (email of secondaryEmails; track email) {
                <mat-list-item class="email-item">
                  <div class="email-item-content">
                    <mat-icon class="email-icon">email</mat-icon>
                    <span class="email-text">{{ email.email }}</span>
                    <mat-chip
                      [class.verified]="email.is_verified"
                      [class.unverified]="!email.is_verified"
                    >
                      {{
                        email.is_verified ? 'Verificeret' : 'Ikke verificeret'
                      }}
                    </mat-chip>
                    <div class="email-actions">
                      <button
                        mat-icon-button
                        color="primary"
                        matTooltip="Gør til primær email"
                        (click)="setAsPrimary(email)"
                        [disabled]="loading || data.readOnly"
                      >
                        <mat-icon>star</mat-icon>
                      </button>
                      <button
                        mat-icon-button
                        color="warn"
                        matTooltip="Slet email"
                        (click)="deleteEmail(email)"
                        [disabled]="loading || data.readOnly"
                      >
                        <mat-icon>delete</mat-icon>
                      </button>
                    </div>
                  </div>
                </mat-list-item>
                }
              </mat-list>
            </div>
            } @else {
            <p class="no-emails">Ingen sekundære emails</p>
            }
          </mat-card-content>
        </mat-card>

        <!-- Add Email Form -->
        @if (!data.readOnly) {
        <mat-card class="email-card">
          <mat-card-header>
            <mat-card-title>Tilføj Ny Email</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <form [formGroup]="addEmailForm" (ngSubmit)="addEmail()">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Email Adresse</mat-label>
                <input
                  matInput
                  formControlName="email"
                  type="email"
                  placeholder="example@domain.com"
                />
                <mat-icon matPrefix>email</mat-icon>
                @if (addEmailForm.get('email')?.hasError('required')) {
                <mat-error> Email er påkrævet </mat-error>
                } @if (addEmailForm.get('email')?.hasError('email')) {
                <mat-error> Ugyldig email adresse </mat-error>
                }
              </mat-form-field>
              <div class="form-actions">
                <button
                  mat-raised-button
                  color="primary"
                  type="submit"
                  [disabled]="addEmailForm.invalid || loading"
                >
                  <mat-icon>add</mat-icon>
                  Tilføj Email
                </button>
              </div>
            </form>
          </mat-card-content>
        </mat-card>
        }

        <!-- User Roles and Permissions -->
        <mat-card class="roles-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>security</mat-icon>
              Roller og Tilladelser
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            @if (allRoles.length > 0) {
            <div class="roles-list">
              @for (role of allRoles; track role) {
              <div class="role-item">
                <mat-chip
                  [class.protected-role]="isProtectedRole(role.roleName)"
                >
                  {{ formatRoleName(role.roleName) }}
                  @if (role.locations && role.locations.length > 0) {
                  <span> ({{ formatRoleLocations(role.locations) }}) </span>
                  }
                </mat-chip>
              </div>
              }
            </div>
            } @else {
            <p class="no-roles">Ingen roller tildelt</p>
            } @if (data.user.protected_roles && data.user.protected_roles.length
            > 0) {
            <div class="protected-roles-info">
              <mat-icon>lock</mat-icon>
              <div>
                <strong>Beskyttede roller:</strong>
                @for (role of data.user.protected_roles; track role; let last =
                $last) {
                <span>
                  {{ formatRoleName(role) }}@if (!last) {
                  <span>, </span>
                  }
                </span>
                }
              </div>
            </div>
            }
          </mat-card-content>
        </mat-card>

        @if (!data.readOnly) {
        <mat-card class="access-groups-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>group_work</mat-icon>
              Adgangsgrupper
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            @if (effectiveAccessGroups.length === 0) {
            <p class="no-roles">Ingen adgangsgrupper tilgængelige for organisationen</p>
            } @else {
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Tildel grupper</mat-label>
              <mat-select multiple [(ngModel)]="selectedAccessGroupIds">
                @for (group of effectiveAccessGroups; track group.id) {
                <mat-option [value]="group.id">
                  {{ group.display_name }} ({{ group.clean_name }})
                </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <button
              mat-stroked-button
              color="primary"
              (click)="saveAccessGroups()"
              [disabled]="loading || savingAccessGroups"
            >
              Gem adgangsgrupper
            </button>
            }
          </mat-card-content>
        </mat-card>
        } @else if (assignedAccessGroups.length > 0) {
        <mat-card class="access-groups-card">
          <mat-card-header>
            <mat-card-title>Adgangsgrupper</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="roles-list">
              @for (g of assignedAccessGroups; track g.id) {
              <mat-chip>{{ g.display_name }}</mat-chip>
              }
            </div>
          </mat-card-content>
        </mat-card>
        }

        <!-- User Status -->
        <mat-card class="status-card">
          <mat-card-header>
            <mat-card-title>Bruger Status</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="status-control">
              <mat-slide-toggle
                [checked]="data.user.is_active ?? true"
                [disabled]="loading || data.readOnly"
                (change)="toggleActiveStatus($event.checked)"
              >
                Bruger er aktiv
              </mat-slide-toggle>
              <p class="status-description">
                Aktiv bruger kan logge ind og bruge systemet. Inaktive brugere
                kan ikke logge ind.
              </p>
            </div>
          </mat-card-content>
        </mat-card>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="close()">Luk</button>
      </mat-dialog-actions>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .email-management-dialog {
        min-width: 600px;
        max-width: 800px;
      }

      h2[mat-dialog-title] {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;
      }

      mat-dialog-content {
        max-height: 70vh;
        overflow-y: auto;
      }

      .warning-banner {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 16px;
        background-color: #fff3cd;
        border-left: 4px solid #ffc107;
        border-radius: 4px;
        margin-bottom: 20px;
      }

      .warning-banner mat-icon {
        color: #ffc107;
        margin-top: 2px;
      }

      .info-banner {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 16px;
        background-color: #e3f2fd;
        border-left: 4px solid #2196f3;
        border-radius: 4px;
        margin-bottom: 20px;
      }

      .info-banner mat-icon {
        color: #2196f3;
        margin-top: 2px;
      }

      .email-card {
        margin-bottom: 20px;
      }

      .primary-email {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
      }

      .primary-email mat-icon {
        color: #ffc107;
      }

      .email-text {
        flex: 1;
        font-weight: 500;
      }

      .email-item-content {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
      }

      .email-item {
        height: auto !important;
        padding: 12px 0 !important;
      }

      .email-icon {
        color: #666;
      }

      .email-actions {
        margin-left: auto;
        display: flex;
        gap: 4px;
      }

      mat-chip.verified {
        background-color: #4caf50;
        color: white;
      }

      mat-chip.unverified {
        background-color: #ff9800;
        color: white;
      }

      .no-emails {
        padding: 20px;
        text-align: center;
        color: #999;
      }

      .full-width {
        width: 100%;
      }

      .form-actions {
        margin-top: 16px;
      }

      .status-card {
        margin-top: 20px;
      }

      .status-control {
        padding: 12px;
      }

      .status-description {
        margin-top: 8px;
        font-size: 12px;
        color: #666;
      }

      .roles-card,
      .access-groups-card {
        margin-top: 20px;
      }

      .full-width {
        width: 100%;
      }

      .roles-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        padding: 12px;
      }

      .role-item {
        display: inline-block;
      }

      mat-chip.protected-role {
        background-color: #ff9800;
        color: white;
      }

      .no-roles {
        padding: 20px;
        text-align: center;
        color: #999;
      }

      .protected-roles-info {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px;
        background-color: #fff3cd;
        border-left: 4px solid #ffc107;
        border-radius: 4px;
        margin-top: 12px;
      }

      .protected-roles-info mat-icon {
        color: #ffc107;
      }
    `,
  ],
})
export class UserEmailManagementDialogComponent implements OnInit {
  addEmailForm: FormGroup;
  secondaryEmails: UserEmailDTO[] = [];
  loading = false;
  allRoles: Array<{ roleName: string; locations?: string[] }> = [];
  effectiveAccessGroups: AccessGroupDTO[] = [];
  selectedAccessGroupIds: number[] = [];
  assignedAccessGroups: UserAccessGroupSummaryDTO[] = [];
  savingAccessGroups = false;

  constructor(
    public dialogRef: MatDialogRef<UserEmailManagementDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      user: UserManagementDTO;
      readOnly: boolean;
    },
    private fb: FormBuilder,
    private http: HttpClient
  ) {
    this.addEmailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit(): void {
    // Compute roles once when component initializes
    this.allRoles = this.computeAllRoles();
    this.assignedAccessGroups = this.data.user.access_groups ?? [];
    this.loadUserEmails();
    this.loadAccessGroupOptions();
  }

  loadAccessGroupOptions(): void {
    const orgId = this.data.user.organizations_id;
    const params =
      orgId != null ? { organizations_id: String(orgId) } : undefined;
    this.http
      .get<{ items: AccessGroupDTO[] }>(
        `${environment.apiUrl}/access-groups/effective`,
        { withCredentials: true, params }
      )
      .subscribe({
        next: (res) => {
          this.effectiveAccessGroups = res.items ?? [];
        },
      });

    this.http
      .get<{ access_groups: UserAccessGroupSummaryDTO[] }>(
        `${environment.apiUrl}/users/manage/${this.data.user.uuid}/access-groups`,
        {
          withCredentials: true,
          params:
            orgId != null ? { organizations_id: String(orgId) } : undefined,
        }
      )
      .subscribe({
        next: (res) => {
          this.assignedAccessGroups = res.access_groups ?? [];
          this.selectedAccessGroupIds = this.assignedAccessGroups.map((g) => g.id);
          this.data.user.access_groups = this.assignedAccessGroups;
        },
      });
  }

  saveAccessGroups(): void {
    if (this.data.readOnly) {
      return;
    }
    this.savingAccessGroups = true;
    const orgId = this.data.user.organizations_id;
    this.http
      .put<{ access_groups: UserAccessGroupSummaryDTO[] }>(
        `${environment.apiUrl}/users/manage/${this.data.user.uuid}/access-groups`,
        { access_group_ids: this.selectedAccessGroupIds },
        {
          withCredentials: true,
          params:
            orgId != null ? { organizations_id: String(orgId) } : undefined,
        }
      )
      .subscribe({
        next: (res) => {
          this.assignedAccessGroups = res.access_groups ?? [];
          this.data.user.access_groups = this.assignedAccessGroups;
          this.savingAccessGroups = false;
        },
        error: () => {
          alert('Kunne ikke gemme adgangsgrupper');
          this.savingAccessGroups = false;
        },
      });
  }

  loadUserEmails(): void {
    this.loading = true;
    this.http
      .get<{ primary_email: string | null; secondary_emails: UserEmailDTO[] }>(
        `${environment.apiUrl}/users/manage/${this.data.user.uuid}/emails`
      )
      .subscribe({
        next: (response) => {
          this.secondaryEmails = response.secondary_emails || [];
          if (response.primary_email != null) {
            this.data.user.user = response.primary_email;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading emails:', error);
          this.loading = false;
        },
      });
  }

  addEmail(): void {
    if (this.addEmailForm.invalid || this.loading) {
      return;
    }

    const email = this.addEmailForm.get('email')?.value?.toLowerCase().trim();
    if (!email) {
      return;
    }

    this.loading = true;
    this.http
      .post(
        `${environment.apiUrl}/users/manage/${this.data.user.uuid}/emails`,
        { email },
        { responseType: 'text' }
      )
      .subscribe({
        next: (responseBody) => {
          this.loading = false;
          try {
            const parsed = JSON.parse(responseBody) as unknown;
            if (
              parsed &&
              typeof parsed === 'object' &&
              'id' in parsed &&
              ('email' in parsed || 'id' in parsed)
            ) {
              this.secondaryEmails.push(parsed as UserEmailDTO);
              this.addEmailForm.reset();
              return;
            }
            if (parsed && typeof parsed === 'object' && 'message' in parsed) {
              alert(
                'Fejl ved tilføjelse af email: ' +
                  (parsed as { message: string }).message
              );
              return;
            }
          } catch {
            // Server returned 200 but body is not JSON (e.g. "Query Error..." from PHP die()).
            // The insert often succeeds before the error; refresh the list so the new email appears.
            this.loadUserEmails();
            this.addEmailForm.reset();
            return;
          }
          this.loadUserEmails();
          this.addEmailForm.reset();
        },
        error: (error) => {
          console.error('Error adding email:', error);
          alert('Fejl ved tilføjelse af email: ' + this.getErrorMessage(error));
          this.loading = false;
        },
      });
  }

  setAsPrimary(email: UserEmailDTO): void {
    if (this.loading || this.data.readOnly) {
      return;
    }

    if (
      !confirm(
        `Er du sikker på at du vil gøre "${email.email}" til primær email? Den nuværende primære email vil blive tilføjet som sekundær.`
      )
    ) {
      return;
    }

    this.loading = true;
    this.http
      .put(
        `${environment.apiUrl}/users/manage/${this.data.user.uuid}/emails/${email.id}/set-primary`,
        {}
      )
      .subscribe({
        next: () => {
          // Reload emails so modal shows new primary and secondary list, then close so parent can refresh
          this.http
            .get<{
              primary_email: string | null;
              secondary_emails: UserEmailDTO[];
            }>(
              `${environment.apiUrl}/users/manage/${this.data.user.uuid}/emails`
            )
            .subscribe({
              next: (response) => {
                this.secondaryEmails = response.secondary_emails || [];
                if (response.primary_email != null) {
                  this.data.user.user = response.primary_email;
                }
                this.loading = false;
                this.dialogRef.close({ refresh: true });
              },
              error: () => {
                this.loading = false;
                this.dialogRef.close({ refresh: true });
              },
            });
        },
        error: (error) => {
          console.error('Error setting primary email:', error);
          const msg = this.getErrorMessage(error);
          alert('Fejl ved ændring af primær email: ' + msg);
          this.loading = false;
        },
      });
  }

  deleteEmail(email: UserEmailDTO): void {
    if (this.loading || this.data.readOnly) {
      return;
    }

    if (
      !confirm(
        `Er du sikker på at du vil slette emailen "${email.email}"? Denne handling kan ikke fortrydes.`
      )
    ) {
      return;
    }

    this.loading = true;
    this.http
      .delete(
        `${environment.apiUrl}/users/manage/${this.data.user.uuid}/emails/${email.id}`
      )
      .subscribe({
        next: () => {
          this.secondaryEmails = this.secondaryEmails.filter(
            (e) => e.id !== email.id
          );
          this.loading = false;
        },
        error: (error) => {
          console.error('Error deleting email:', error);
          alert('Fejl ved sletning af email: ' + this.getErrorMessage(error));
          this.loading = false;
        },
      });
  }

  toggleActiveStatus(isActive: boolean): void {
    if (this.loading || this.data.readOnly) {
      return;
    }

    this.loading = true;
    this.http
      .put(
        `${environment.apiUrl}/users/manage/${this.data.user.uuid}/activate`,
        { is_active: isActive }
      )
      .subscribe({
        next: () => {
          this.data.user.is_active = isActive;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error updating user status:', error);
          alert(
            'Fejl ved opdatering af bruger status: ' +
              this.getErrorMessage(error)
          );
          this.loading = false;
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  /**
   * Extract user-facing error message from API error.
   * Handles both JSON error bodies (e.g. { message: "..." }) and plain text (e.g. "Query Error: ...").
   */
  private getErrorMessage(error: {
    error?: unknown;
    message?: string;
  }): string {
    const e = error?.error;
    if (
      e != null &&
      typeof e === 'object' &&
      typeof (e as { message?: string }).message === 'string'
    ) {
      return (e as { message: string }).message;
    }
    if (typeof e === 'string' && e.length > 0) {
      return e;
    }
    return error?.message ?? 'Ukendt fejl';
  }

  /**
   * Compute all roles for the user, formatted for display
   * This is called once in ngOnInit and cached in allRoles property
   */
  private computeAllRoles(): Array<{ roleName: string; locations?: string[] }> {
    if (!this.data.user.roles || typeof this.data.user.roles !== 'object') {
      return [];
    }

    const roles: Array<{ roleName: string; locations?: string[] }> = [];
    const rolesObj = this.data.user.roles;

    // Handle both object and array formats
    if (Array.isArray(rolesObj)) {
      // Numeric array format: ["orgadmin", "bruger"]
      rolesObj.forEach((role) => {
        if (typeof role === 'string') {
          roles.push({ roleName: role });
        }
      });
    } else {
      // Object format: {"orgadmin": ["true"], "bruger": ["location1"]}
      Object.keys(rolesObj).forEach((roleName) => {
        const roleValue = rolesObj[roleName];
        if (
          roleValue === true ||
          (Array.isArray(roleValue) && roleValue.length > 0)
        ) {
          // Extract locations if it's an array (excluding ["true"])
          const locations =
            Array.isArray(roleValue) &&
            roleValue.length > 0 &&
            roleValue[0] !== 'true'
              ? roleValue
              : undefined;
          roles.push({
            roleName,
            locations: locations as string[] | undefined,
          });
        }
      });
    }

    return roles;
  }

  /**
   * Check if a role is a protected role
   */
  isProtectedRole(roleName: string): boolean {
    const protectedRoles = ['orgbruger', 'orgadmin', 'superadmin'];
    return protectedRoles.includes(roleName.toLowerCase());
  }

  /**
   * Format role name for display (capitalize first letter)
   */
  formatRoleName(roleName: string): string {
    return roleName.charAt(0).toUpperCase() + roleName.slice(1);
  }

  /**
   * Format role names array for display
   */
  formatRoleNames(roleNames: string[]): string {
    return roleNames.map((role) => this.formatRoleName(role)).join(', ');
  }

  /**
   * Format role locations for display
   */
  formatRoleLocations(locations: string[]): string {
    if (!locations || locations.length === 0) {
      return '';
    }
    // If locations contains "true", it's a global role
    if (locations.includes('true')) {
      return 'Global';
    }
    return locations.join(', ');
  }
}
